import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  Chip,
  OutlinedInput,
  Paper,
  Skeleton,
  Stack,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { SwayyCatalogOptionsStore } from "../../../stores/SwayyCatalogOptionsStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import { SwayyPageHeader } from "../../components/SwayyPageShell";

const primaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
  color: swayyColors.white,
  backgroundColor: swayyColors.pine,
  "&:hover": {
    backgroundColor: swayyColors.pineHover,
    color: swayyColors.white,
  },
  "&.Mui-disabled": {
    color: swayyColors.white,
    opacity: 0.55,
  },
  alignSelf: "flex-start",
} as const;

const secondaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
} as const;

const inputSx = {
  borderRadius: swayyRadii.md,
  fontFamily: swayyFont,
  fontSize: 13,
  height: 32,
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-input": {
    py: 0.5,
    height: 32,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: swayyColors.border },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
} as const;

const SwayyCatalogOptionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const optionId = Number(id);
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const storeRef = useRef(new SwayyCatalogOptionsStore());
  const store = storeRef.current;
  const canViewCatalog = userStore.hasSwayyPermission([
    SwayyEmployeeRoles.VIEW_CATALOG,
    SwayyEmployeeRoles.EDIT_CATALOG,
  ]);
  const canEditCatalog = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_CATALOG,
  );

  useEffect(() => {
    if (!canViewCatalog) return;
    if (!Number.isInteger(optionId) || optionId <= 0) return;
    void store.loadOption(optionId).then((error) => {
      if (error) {
        showErrorRef.current("Error", "Failed to load option.");
      }
    });
    return () => store.dispose();
  }, [canViewCatalog, optionId, store]);

  const detail = store.optionDetail;
  const isSwatch =
    String(detail?.displayType || "").toLowerCase() === "swatch";
  const isEditing = store.editingValueId != null;

  const handleSaveValue = async () => {
    if (!detail) return;
    try {
      if (!store.canAddOptionValue) {
        showError("Validation", "Value and SKU code are required.");
        return;
      }
      const error = await store.saveOptionValue(detail.id, isSwatch);
      if (error) {
        showError(
          isEditing ? "Unable to update value" : "Unable to add value",
          error.message,
        );
        return;
      }
      showSuccess(isEditing ? "Value updated." : "Value added.");
    } catch (error) {
      showError(
        isEditing ? "Unable to update value" : "Unable to add value",
        error instanceof Error ? error.message : "Something went wrong.",
      );
    }
    const error = await store.createOptionValue(detail.id, isSwatch);
    if (error) {
      showError("Error", "Failed to add value.");
      return;
    }
    showSuccess("Value added.");
  };

  if (!canViewCatalog) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <SwayyPageHeader
        title={detail?.name || "Option"}
        description="Add and manage values for this option."
        actions={
          <AppButton
            label="Back to options"
            variant="secondary"
            onClick={() => navigate("/catalog-options")}
            customStyles={secondaryBtnSx}
          />
        }
      />
      {store.detailState === "loading" || store.detailState === "idle" ? (
        <Skeleton variant="rounded" height={220} />
      ) : !detail ? (
        <CustomTypography variant="body1" color={swayyColors.danger}>
          Option not found.
        </CustomTypography>
      ) : (
        <Stack spacing={2}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: swayyRadii.lg,
              borderColor: swayyColors.border,
            }}
          >
            <CustomTypography
              variant="body2"
              color={swayyColors.textSecondary}
            >
              Display: {detail.displayType} ·{" "}
              {detail.isActive ? "Active" : "Inactive"}
            </CustomTypography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
              sx={{ mt: 1.5 }}
            >
              {detail.values.length === 0 ? (
                <CustomTypography
                  variant="body2"
                  color={swayyColors.textSecondary}
                >
                  No values yet.
                </CustomTypography>
              ) : (
                detail.values.map((item) => {
                  const hex = item.hex?.trim();
                  const hexLabel = hex
                    ? hex.startsWith("#")
                      ? hex
                      : `#${hex}`
                    : "";
                  const label = [
                    item.name,
                    item.skuCode || null,
                    hexLabel || null,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <Chip
                      key={item.id}
                      size="small"
                      label={label}
                    onDelete={
                      canEditCatalog
                        ? () => store.startEditValue(item)
                        : undefined
                    }
                    deleteIcon={
                      canEditCatalog ? (
                        <EditOutlinedIcon sx={{ fontSize: 14 }} />
                      ) : undefined
                    }
                    sx={{
                      height: 28,
                      fontFamily: swayyFont,
                      fontSize: 12,
                      backgroundColor: swayyColors.pineLight,
                      color: swayyColors.deepGreen,
                      fontWeight: 600,
                      "& .MuiChip-deleteIcon": {
                        color: swayyColors.deepGreen,
                        fontSize: 14,
                        "&:hover": { color: swayyColors.pine },
                      },
                    }}
                  />
                  );
                })
              )}
            </Stack>
          </Paper>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: swayyRadii.lg,
              borderColor: swayyColors.border,
            }}
          >
            <CustomTypography
              variant="heading5"
              color={swayyColors.deepGreen}
              sx={{ fontWeight: 700, mb: 1.5 }}
            >
              {canEditCatalog ? "Add value" : "Values"}
            </CustomTypography>
            {canEditCatalog ? (
              <Stack spacing={1.25} maxWidth={420}>
                <OutlinedInput
                  value={store.addValueForm.value}
                  onChange={(event) =>
                    store.updateAddValueForm("value", event.target.value)
                  }
                  placeholder="Value"
                  fullWidth
                  sx={inputSx}
                />
                <OutlinedInput
                  value={store.addValueForm.skuCode}
                  onChange={(event) =>
                    store.updateAddValueForm("skuCode", event.target.value)
                  }
                  placeholder="SKU code"
                  fullWidth
                  sx={inputSx}
                />
                {isSwatch && (
                  <OutlinedInput
                    value={store.addValueForm.hex}
                    onChange={(event) =>
                      store.updateAddValueForm("hex", event.target.value)
                    }
                    placeholder="#RRGGBB"
                    fullWidth
                    sx={inputSx}
                  />
                )}
                <Stack direction="row" spacing={1}>
                  {isEditing ? (
                    <AppButton
                      label="Cancel"
                      variant="secondary"
                      disabled={store.isSaving}
                      onClick={() => store.cancelEditValue()}
                      customStyles={secondaryBtnSx}
                    />
                  ) : null}
                  <AppButton
                    label={
                      store.isSaving
                        ? isEditing
                          ? "Saving..."
                          : "Adding..."
                        : isEditing
                          ? "Save"
                          : "Add value"
                    }
                    variant="primary"
                    disabled={store.isSaving}
                    onClick={() => void handleSaveValue()}
                    customStyles={primaryBtnSx}
                  />
                </Stack>
              </Stack>
            ) : (
              <CustomTypography
                variant="body2"
                color={swayyColors.textSecondary}
              >
                You have view-only access to this option.
              </CustomTypography>
            )}
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

export default observer(SwayyCatalogOptionDetail);
